import type { KgFlowNodeType } from "../../components/kg/canvas/types";
import type {
  KgDataSourceTableRow,
  KgMetricOntologyLink,
  KgOntologyDataSourceItem,
  KgOntologyOverviewRefValue,
  KgOntologyReportApiRow,
  KgOntologyReportRow,
  KgSchemaMetricItem,
  KgSchemaRelationshipRow,
  KgUsedInItem,
} from "./kgDrawerApi.types";
import {
  isSampleGraphCanvasId,
  parseFocusIdFromHref,
  toDataSourceCanvasNodeId,
  toMetricCanvasNodeId,
  toOntologyCanvasNodeId,
  toReportCanvasNodeId,
  toStoryCanvasNodeId,
  toTableCanvasNodeId,
} from "./kgNodeIds";

/** Target drawer row shape — align BE responses to this over time. */
export interface KgApiNavLink {
  id: string;
  name: string;
  type?: KgFlowNodeType;
  href?: string | null;
  externalUrl?: string | null;
}

/** UI row for any inspector link that can focus the canvas or open externally. */
export interface KgInspectorNavLink {
  label: string;
  canvasNodeId?: string;
  externalUrl?: string;
}

export const isExternalHttpUrl = (url?: string | null): boolean =>
  !!url && /^https?:\/\//i.test(url);

/** True when href carries an explicit canvas focus id (`?focus=`). */
export const isInspectorFocusHref = (href?: string | null): boolean =>
  !!href && parseFocusIdFromHref(href) !== undefined;

const CANVAS_ID_BUILDERS: Partial<Record<KgFlowNodeType, (id: string) => string>> =
  {
    dataSource: toDataSourceCanvasNodeId,
    table: toTableCanvasNodeId,
    ontology: toOntologyCanvasNodeId,
    metric: toMetricCanvasNodeId,
    report: toReportCanvasNodeId,
    dataStory: toStoryCanvasNodeId,
  };

const mapApiEntityTypeToFlowType = (
  apiType?: string,
): KgFlowNodeType | undefined => {
  switch (apiType) {
    case "dataSource":
      return "dataSource";
    case "schema":
      return "table";
    case "ontology":
      return "ontology";
    case "metric":
      return "metric";
    case "report":
      return "report";
    case "story":
      return "dataStory";
    default:
      return undefined;
  }
};

/**
 * Single factory for inspector → canvas navigation.
 * Order: href ?focus= → explicit type + id prefix → section type + id prefix.
 */
export const toInspectorNavLink = (
  item: KgApiNavLink,
  sectionType?: KgFlowNodeType,
): KgInspectorNavLink => {
  const flowType = item.type ?? sectionType;
  const builder = flowType ? CANVAS_ID_BUILDERS[flowType] : undefined;
  const fromHref = item.href ? parseFocusIdFromHref(item.href) : undefined;
  const fromSampleId = isSampleGraphCanvasId(item.id) ? item.id : undefined;

  return {
    label: item.name,
    canvasNodeId:
      fromHref ??
      fromSampleId ??
      (builder ? builder(item.id) : undefined),
    externalUrl: isExternalHttpUrl(item.externalUrl) ? item.externalUrl : undefined,
  };
};

// ─── Legacy API → KgApiNavLink normalizers (remove when BE emits KgApiNavLink) ─

export const normalizeUsedInItem = (item: KgUsedInItem): KgApiNavLink => ({
  id: item.id,
  name: item.name,
  href: item.href,
  externalUrl: item.externalUrl,
});

export const normalizeSchemaMetricItem = (
  item: KgSchemaMetricItem,
): KgApiNavLink => ({
  id: item.id,
  name: item.name,
  href: item.href ?? undefined,
  externalUrl: item.externalUrl ?? undefined,
});

export const normalizeDataSourceTableRow = (
  row: KgDataSourceTableRow,
): KgApiNavLink => ({
  id: row.id,
  name: row.name,
  type: "table",
});

export const normalizeSchemaRelationshipRow = (
  row: KgSchemaRelationshipRow,
): KgApiNavLink => ({
  id: row.id,
  name: row.name,
  href: row.nameHref,
});

export const normalizeOntologyDataSourceItem = (
  item: KgOntologyDataSourceItem,
): KgApiNavLink => ({
  id: item.id,
  name: item.tableName,
  externalUrl: item.externalUrl,
});

export const normalizeOntologyOverviewRef = (
  ref: KgOntologyOverviewRefValue,
): KgApiNavLink => ({
  id: ref.id,
  name: ref.name,
  type: mapApiEntityTypeToFlowType(ref.type),
  href: ref.href,
  externalUrl: ref.externalUrl,
});

export const normalizeMetricOntologyLink = (
  item: KgMetricOntologyLink,
): KgApiNavLink => ({
  id: item.id,
  name: item.name,
  externalUrl: item.externalUrl,
});

export const normalizeOntologyReportRow = (
  row: KgOntologyReportRow | KgOntologyReportApiRow,
): KgApiNavLink => {
  const title =
    "title" in row && row.title
      ? row.title
      : "name" in row && row.name
        ? row.name
        : "";
  return {
    id: row.id,
    name: title,
    href: "nameHref" in row ? row.nameHref : undefined,
    externalUrl: "externalUrl" in row ? row.externalUrl : undefined,
  };
};
